import { useCallback, useId, useMemo, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  Button,
  Card,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  Icon,
  TooltipProvider,
  cn,
} from "@nexus/ui";
import {
  MIN_SCORED_CREATIVES_FOR_INSIGHTS,
  type PreflightMarketKind,
} from "@nexus/contracts";
import type { PreflightScoreBand } from "../../types/preflight.types";
import {
  TONE_BG,
  TONE_TEXT,
  type ResultTone,
} from "../../constants/result-state";
import { useHoverIntent } from "../../hooks/useHoverIntent";
import {
  anchorForRect,
  useHoverPanelAnchor,
} from "../../hooks/useHoverPanelAnchor";
import { bandTone, scoreToBand } from "../../lib/score-band";
import {
  featureDisplayTone,
  featureNote,
  featureResultLabel,
  weightPercent,
} from "../../lib/feature-format";
import type {
  CriteriaMatrix,
  MatrixCell,
  MatrixColumn,
  MatrixColumnKind,
  MatrixRow,
} from "../../lib/report-matrix";
import { AssetNameLabel } from "../AssetNameLabel/AssetNameLabel";
import { AssetThumbnail } from "../AssetThumbnail/AssetThumbnail";
import { BandPill } from "../BandPill/BandPill";
import { CollapsibleSection } from "../CollapsibleSection/CollapsibleSection";
import { CriterionLabel } from "../CriterionLabel/CriterionLabel";
import { HoverPanel } from "../HoverPanel/HoverPanel";
import { MarketIcon } from "../MarketIcon/MarketIcon";

/**
 * Catalogue kind for a market column's icon. Prefers the insights payload; when a
 * stale client cache omits `kind`, fall back from the code so a country still gets
 * its flag (continents without kind briefly share that path until refetch).
 */
function marketColumnIconKind(
  column: MatrixColumn,
): PreflightMarketKind | null {
  if (column.kind) return column.kind;
  if (!column.code) return null;
  return column.code === "GLOBAL" ? "GLOBAL" : "COUNTRY";
}

const DASH = "–";

/** Column ordering for the creative axis (mirrors the creatives-list sort options). */
type ColumnSort = "default" | "score-desc" | "score-asc";

/** A score column fits a two-digit score; a market code or report name needs room to read. */
const CREATIVE_COLUMN_WIDTH = "minmax(1.75rem, 1fr)";
/**
 * Labelled columns share leftover width so a few markets still fill the card;
 * the header content stays a centered cluster so a wide column doesn't look empty.
 */
const LABELLED_COLUMN_WIDTH = "minmax(4.75rem, 1fr)";
/**
 * Many labelled columns keep a fixed track so they overflow into a horizontal
 * scroll instead of being crushed by `1fr` inside a clipped card.
 */
const LABELLED_COLUMN_WIDTH_FIXED = "4.75rem";
/**
 * At or below this many labelled columns each still has room for flag + full name
 * on one row; beyond it headers stack flag above the catalogue code.
 */
const MAX_COLUMNS_FOR_FULL_NAME = 4;

const COLUMN_SORT_LABEL_KEYS: Record<ColumnSort, string> = {
  default: "preflight.creatives.sortDefault",
  "score-desc": "preflight.creatives.sortScoreDesc",
  "score-asc": "preflight.creatives.sortScoreAsc",
};

/** Aggregate axes use "As listed" for the unsorted option; creatives keep report order. */
const AGGREGATE_SORT_LABEL_KEYS: Record<ColumnSort, string> = {
  default: "preflight.heatmap.sortListed",
  "score-desc": "preflight.creatives.sortScoreDesc",
  "score-asc": "preflight.creatives.sortScoreAsc",
};

interface ActiveCell {
  rowKey: string;
  columnId: string;
}

/** Stable hover-intent key; `\0` cannot appear in criterion or column ids. */
function cellHoverKey(rowKey: string, columnId: string): string {
  return `${rowKey}\0${columnId}`;
}

function parseCellHoverKey(key: string | null): ActiveCell | null {
  if (!key) return null;
  const sep = key.indexOf("\0");
  if (sep < 0) return null;
  return { rowKey: key.slice(0, sep), columnId: key.slice(sep + 1) };
}

/** Score that drives column sort and header chrome for this axis. */
function columnSortScore(
  column: MatrixColumn,
  columnKind: MatrixColumnKind,
): number | null | undefined {
  return columnKind === "CREATIVE" ? column.score : column.avgScore;
}

/** Index permutation that orders the matrix columns by the chosen score sort. */
function columnOrder(
  columns: MatrixColumn[],
  sort: ColumnSort,
  columnKind: MatrixColumnKind,
): number[] {
  const indices = columns.map((_, index) => index);
  if (sort === "default") return indices;
  const direction = sort === "score-desc" ? -1 : 1;
  return indices.sort((a, b) => {
    const scoreA = columnSortScore(columns[a], columnKind);
    const scoreB = columnSortScore(columns[b], columnKind);
    // Unscored columns always sink to the end regardless of direction.
    if (scoreA == null && scoreB == null) return 0;
    if (scoreA == null) return 1;
    if (scoreB == null) return -1;
    return (scoreA - scoreB) * direction;
  });
}

/** Compact score-sort dropdown for heatmap columns. */
function ColumnSortMenu({
  sort,
  onSortChange,
  isCreativeAxis,
}: {
  sort: ColumnSort;
  onSortChange: (sort: ColumnSort) => void;
  isCreativeAxis: boolean;
}) {
  const { t } = useTranslation();
  const labelKeys = isCreativeAxis
    ? COLUMN_SORT_LABEL_KEYS
    : AGGREGATE_SORT_LABEL_KEYS;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          iconStart="sliders-horizontal"
          className="text-muted-foreground shrink-0 gap-1.5"
          aria-label={
            isCreativeAxis
              ? t("preflight.creatives.sortAria")
              : t("preflight.heatmap.sortAria")
          }
        >
          {t(labelKeys[sort])}
          <Icon name="chevron-down" size="xs" color="inherit" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuRadioGroup
          value={sort}
          onValueChange={(value) => onSortChange(value as ColumnSort)}
        >
          <DropdownMenuRadioItem value="default">
            {t(labelKeys.default)}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="score-desc">
            {t(labelKeys["score-desc"])}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="score-asc">
            {t(labelKeys["score-asc"])}
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Verdicts behind an aggregate cell; null when nothing landed or a single verdict owns it. */
function cellVerdicts(
  cell: MatrixCell,
): { missed: number; total: number } | null {
  if (cell.missRate == null) return null;
  const passes = cell.passes ?? 0;
  const failures = cell.failures ?? 0;
  const partials = cell.partials ?? 0;
  return { missed: failures + partials, total: passes + failures + partials };
}

/** Verdicts behind one column, so its header panel can say how that column landed overall. */
export interface ColumnTally {
  passes: number;
  partials: number;
  failures: number;
}

/**
 * Tally a column down the criteria rows. Report-detail cells carry one creative's verdict;
 * aggregate cells carry the counts behind a market or report, so both sum the same way.
 */
function columnTally(rows: MatrixRow[], columnIndex: number): ColumnTally {
  const tally: ColumnTally = { passes: 0, partials: 0, failures: 0 };
  for (const row of rows) {
    const cell = row.cells[columnIndex];
    if (!cell) continue;
    if (cell.feature) {
      if (cell.feature.state === "PASS") tally.passes += 1;
      else if (cell.feature.state === "PARTIAL") tally.partials += 1;
      else if (cell.feature.state === "FAIL") tally.failures += 1;
      continue;
    }
    tally.passes += cell.passes ?? 0;
    tally.partials += cell.partials ?? 0;
    tally.failures += cell.failures ?? 0;
  }
  return tally;
}

/** Tone for a swatch: the feature's result tone, or a neutral track when absent/excluded. */
function cellTone(cell: MatrixCell): ResultTone | null {
  if (cell.feature) return featureDisplayTone(cell.feature);
  if (cell.missRate == null) return null;
  // Aggregate cells have no single verdict: clean reads as a pass, every-creative-missed
  // as a fail, and anything between as partial.
  if (cell.missRate <= 0) return "success";
  if (cell.missRate >= 1) return "error";
  return "warning";
}

/** What a cell reports: the creative's verdict, or how often the column missed. */
function cellResultLabel(cell: MatrixCell, t: TFunction): string {
  if (cell.feature) return featureResultLabel(cell.feature, t);
  const verdicts = cellVerdicts(cell);
  if (!verdicts) return t("preflight.heatmap.absent");
  return t("preflight.blockers.missedOf", {
    count: verdicts.missed,
    total: verdicts.total,
  });
}

export interface CriteriaHeatmapProps {
  /** The grid to render — built from one report, or served by the market aggregate. */
  matrix: CriteriaMatrix;
  /** Scored creatives in scope: the description count and the compare gate. */
  scoredCount: number;
  /**
   * Column header click. The id is a creative, market, or report id per the matrix's
   * `columnKind`, so the caller decides whether that scrolls, scopes, or navigates (R22).
   */
  onSelectColumn?: (columnId: string) => void;
  /** Jump to a specific criterion row inside a column (cell click). */
  onSelectCriterion?: (columnId: string, criterionKey: string) => void;
  /** Render just the panel body (no collapsible section) — used inside the insights tabs. */
  headless?: boolean;
  className?: string;
}

/**
 * Criteria × creatives matrix: each cell a pass/partial/fail swatch. Reveals systemic
 * weaknesses ("every creative fails Captions") that per-card tables hide — rows are ranked
 * so the most-missed criteria sit on top. Hovering a cell opens a detail panel at the
 * swatch and mirrors a compact readout below; the per-card breakdown tables remain the
 * accessible source of truth. Renders nothing until at least two creatives can be compared.
 */
export function CriteriaHeatmap({
  matrix,
  scoredCount,
  onSelectColumn,
  onSelectCriterion,
  headless = false,
  className,
}: CriteriaHeatmapProps) {
  const { t } = useTranslation();
  // Market/report axes default to worst-first so left = weakest; creatives keep report order.
  const [sort, setSort] = useState<ColumnSort>(() =>
    matrix.columnKind === "CREATIVE" ? "default" : "score-asc",
  );

  // One hover panel serves every column header, keyed by column id.
  const hover = useHoverIntent<string>();
  const { anchor, pinTo } = useHoverPanelAnchor(hover.openKey !== null);
  // One hover panel serves every cell, keyed by row + column.
  const cellHover = useHoverIntent<string>();
  const { anchor: cellAnchor, pinTo: pinCell } = useHoverPanelAnchor(
    cellHover.openKey !== null,
  );
  const panelIdPrefix = useId();
  const cellPanelId = `${panelIdPrefix}-cell`;
  const openTriggerRef = useRef<HTMLElement | null>(null);
  const cellTriggerRef = useRef<HTMLElement | null>(null);
  const { open, openNow, close, closeNow, keepOpen } = hover;
  const {
    open: openCellIntent,
    openNow: openCellNow,
    close: closeCell,
    closeNow: closeCellNow,
    keepOpen: keepCellOpen,
  } = cellHover;

  const active = parseCellHoverKey(cellHover.openKey);

  const openPanel = useCallback(
    (columnId: string, trigger: HTMLElement, immediate: boolean) => {
      closeCellNow();
      openTriggerRef.current = trigger;
      pinTo(() => anchorForRect(trigger.getBoundingClientRect(), 50, "top"));
      if (immediate) openNow(columnId);
      else open(columnId);
    },
    [closeCellNow, open, openNow, pinTo],
  );

  const dismissPanel = useCallback(() => {
    closeNow();
    openTriggerRef.current?.focus();
  }, [closeNow]);

  const openCellPanel = useCallback(
    (cell: ActiveCell, trigger: HTMLElement, immediate: boolean) => {
      closeNow();
      cellTriggerRef.current = trigger;
      pinCell(() => anchorForRect(trigger.getBoundingClientRect()));
      const key = cellHoverKey(cell.rowKey, cell.columnId);
      if (immediate) openCellNow(key);
      else openCellIntent(key);
    },
    [closeNow, openCellIntent, openCellNow, pinCell],
  );

  const dismissCellPanel = useCallback(() => {
    closeCellNow();
    cellTriggerRef.current?.focus();
  }, [closeCellNow]);

  const isCreativeAxis = matrix.columnKind === "CREATIVE";

  // Reorder columns by score; rows keep their ranking. Cells align by index, so the same
  // permutation reorders both the headers and each row's swatches.
  const order = useMemo(
    () => columnOrder(matrix.columns, sort, matrix.columnKind),
    [matrix.columns, matrix.columnKind, sort],
  );
  const orderedColumns = useMemo(
    () => order.map((index) => matrix.columns[index]),
    [order, matrix.columns],
  );

  if (
    scoredCount < MIN_SCORED_CREATIVES_FOR_INSIGHTS ||
    matrix.rows.length === 0
  )
    return null;

  // Give criterion labels a generous, bounded column; many columns push the swatches into
  // a horizontal scroll rather than starving the labels (they truncate with a title fallback).
  const isCrowdedLabelledAxis =
    !isCreativeAxis && matrix.columns.length > MAX_COLUMNS_FOR_FULL_NAME;
  const columnWidth = isCreativeAxis
    ? CREATIVE_COLUMN_WIDTH
    : isCrowdedLabelledAxis
      ? LABELLED_COLUMN_WIDTH_FIXED
      : LABELLED_COLUMN_WIDTH;
  const gridTemplateColumns = `minmax(11rem, 14rem) repeat(${matrix.columns.length}, ${columnWidth})`;

  const activeRow = active
    ? (matrix.rows.find((r) => r.key === active.rowKey) ?? null)
    : null;
  const activeCell =
    active && activeRow
      ? (activeRow.cells[
          matrix.columns.findIndex((c) => c.id === active.columnId)
        ] ?? null)
      : null;
  const activeColumn = active
    ? (matrix.columns.find((c) => c.id === active.columnId) ?? null)
    : null;
  const activeVerdicts = activeCell ? cellVerdicts(activeCell) : null;
  const hoverColumn = hover.openKey
    ? (orderedColumns.find((column) => column.id === hover.openKey) ?? null)
    : null;
  const hoverColumnIndex = hoverColumn
    ? orderedColumns.findIndex((column) => column.id === hoverColumn.id)
    : -1;
  const hoverTally =
    hoverColumnIndex >= 0
      ? columnTally(matrix.rows, order[hoverColumnIndex]!)
      : null;

  const body = (
    <>
      <div className="mb-4 flex items-start justify-between gap-3">
        <p className="text-muted-foreground text-sm">
          {t("preflight.heatmap.description", { count: scoredCount })}
        </p>
        <ColumnSortMenu
          sort={sort}
          onSortChange={setSort}
          isCreativeAxis={isCreativeAxis}
        />
      </div>

      <TooltipProvider delayDuration={200} skipDelayDuration={100}>
        <div
          className="w-0 min-w-full overflow-x-auto overflow-y-clip"
          onMouseLeave={() => {
            closeCell();
            close();
          }}
        >
          <div
            className="grid w-max min-w-full gap-1"
            style={{ gridTemplateColumns }}
          >
            <div
              aria-hidden
              className="text-muted-foreground self-end pb-1 text-[11px] font-medium tracking-wide uppercase"
            >
              {t("preflight.heatmap.criteriaHeader")}
            </div>
            {orderedColumns.map((column) => (
              <ColumnHeader
                key={column.id}
                column={column}
                columnKind={matrix.columnKind}
                showFullName={
                  !isCreativeAxis &&
                  matrix.columns.length <= MAX_COLUMNS_FOR_FULL_NAME
                }
                dimmed={active != null && active.columnId !== column.id}
                hover={{
                  isOpen: hover.openKey === column.id,
                  panelId: `${panelIdPrefix}-${column.id}`,
                  onOpen: (trigger, immediate) =>
                    openPanel(column.id, trigger, immediate),
                  onClose: close,
                }}
                onSelect={onSelectColumn}
              />
            ))}

            {matrix.rows.map((row, rowIndex) => (
              <RowCells
                key={row.key}
                rowKey={row.key}
                rowIndex={rowIndex}
                name={row.name}
                definition={row.definition}
                cells={order.map((index) => row.cells[index])}
                columns={orderedColumns}
                active={active}
                rowActive={active?.rowKey === row.key}
                cellPanelId={cellPanelId}
                onActivate={openCellPanel}
                onDeactivate={closeCell}
                onSelect={onSelectCriterion}
                t={t}
              />
            ))}
          </div>

          {hoverColumn && hoverTally ? (
            <HoverPanel
              id={`${panelIdPrefix}-${hoverColumn.id}`}
              anchor={anchor}
              label={hoverColumn.label}
              isInteractive
              onPointerEnter={keepOpen}
              onPointerLeave={close}
              onEscape={dismissPanel}
            >
              <ColumnHeaderPanel
                column={hoverColumn}
                columnKind={matrix.columnKind}
                tally={hoverTally}
                isSelectable={onSelectColumn != null}
              />
            </HoverPanel>
          ) : null}

          {activeCell && activeColumn && activeRow ? (
            <HoverPanel
              id={cellPanelId}
              anchor={cellAnchor}
              label={`${activeRow.name} · ${activeColumn.label}`}
              isInteractive={onSelectCriterion != null}
              onPointerEnter={keepCellOpen}
              onPointerLeave={closeCell}
              onEscape={dismissCellPanel}
            >
              <CellHoverPanel
                criterion={activeRow.name}
                definition={activeRow.definition}
                column={activeColumn}
                columnKind={matrix.columnKind}
                cell={activeCell}
                weight={
                  activeCell.feature
                    ? weightPercent(activeCell.feature.weight)
                    : weightPercent(activeRow.weight)
                }
                isSelectable={onSelectCriterion != null}
              />
            </HoverPanel>
          ) : null}
        </div>
      </TooltipProvider>

      <div className="mt-3 flex min-h-5 items-center border-t border-border/60 pt-3 text-xs">
        {activeCell?.feature && activeColumn && activeRow ? (
          <CellReadout
            criterion={activeRow.name}
            columnLabel={activeColumn.label}
            tone={featureDisplayTone(activeCell.feature)}
            result={featureResultLabel(activeCell.feature, t)}
            weight={weightPercent(activeCell.feature.weight)}
            note={featureNote(activeCell.feature, t)}
          />
        ) : activeCell && activeVerdicts && activeColumn && activeRow ? (
          <CellReadout
            criterion={activeRow.name}
            columnLabel={activeColumn.label}
            tone={cellTone(activeCell) ?? "muted"}
            result={t("preflight.blockers.missedOf", {
              count: activeVerdicts.missed,
              total: activeVerdicts.total,
            })}
            weight={weightPercent(activeRow.weight)}
          />
        ) : active && activeColumn && activeRow ? (
          <span className="text-muted-foreground min-w-0 truncate">
            {t("preflight.heatmap.notEvaluated", {
              criterion: activeRow.name,
              asset: activeColumn.label,
            })}
          </span>
        ) : (
          <Legend />
        )}
      </div>
    </>
  );

  if (headless) return body;

  return (
    <CollapsibleSection
      title={t("preflight.heatmap.title")}
      className={className}
    >
      <Card className="gap-0 border-border/60 p-5">{body}</Card>
    </CollapsibleSection>
  );
}

/** Wiring a header needs to drive the one hover panel shared by every column. */
interface ColumnHoverProps {
  isOpen: boolean;
  panelId: string;
  /** Open pinned to this trigger; keyboard focus and taps skip the dwell delay. */
  onOpen: (trigger: HTMLElement, immediate: boolean) => void;
  onClose: () => void;
}

function ColumnHeader({
  column,
  columnKind,
  showFullName,
  dimmed,
  hover,
  onSelect,
}: {
  column: MatrixColumn;
  columnKind: MatrixColumnKind;
  /** Few labelled columns: flag + full name on one row. Many: flag above catalogue code. */
  showFullName: boolean;
  dimmed: boolean;
  hover: ColumnHoverProps;
  onSelect?: (columnId: string) => void;
}) {
  const { t } = useTranslation();
  const isCreative = columnKind === "CREATIVE";
  const score = columnSortScore(column, columnKind);
  const roundedScore = score != null ? Math.round(score) : null;
  const tone = scoreTone(score, column.band);
  const iconKind =
    columnKind === "MARKET" ? marketColumnIconKind(column) : null;
  const ariaLabel =
    roundedScore != null
      ? t(
          isCreative
            ? "preflight.heatmap.creativeColumnAria"
            : "preflight.heatmap.columnAria",
          { name: column.label, score: roundedScore },
        )
      : column.label;

  // Creatives: score only (matches Score spread). Aggregates: tone-coloured score + quiet
  // "avg" — scale lives in aria/hover ("out of 100"), not repeated in every header.
  const scoreMark =
    roundedScore != null ? (
      <span
        className={cn(
          "inline-flex items-center gap-1 tabular-nums",
          isCreative ? "text-[13px] font-semibold" : "text-sm font-semibold",
          TONE_TEXT[tone],
        )}
      >
        <span>{roundedScore}</span>
        {!isCreative ? (
          <span
            className="text-muted-foreground text-[11px] font-normal"
            title={t("preflight.avgScore")}
          >
            {t("preflight.heatmap.avgAbbrev")}
          </span>
        ) : null}
      </span>
    ) : (
      <span
        className={cn(
          "tabular-nums",
          isCreative ? "text-[13px] font-semibold" : "text-sm font-semibold",
          TONE_TEXT[tone],
        )}
      >
        {DASH}
      </span>
    );

  const content = isCreative ? (
    scoreMark
  ) : (
    <span className="flex max-w-full flex-col items-center gap-0.5 px-0.5">
      <span
        className={cn(
          "flex max-w-full items-center gap-0.5",
          showFullName ? "flex-row gap-1.5" : "flex-col",
        )}
      >
        {iconKind && column.code ? (
          <MarketIcon
            kind={iconKind}
            code={column.code}
            name={column.label}
            className="h-3.5 w-[1.3rem] shrink-0 rounded-sm shadow-[0_0_0_1px_var(--border)]"
          />
        ) : null}
        {showFullName ? (
          <span
            className="text-foreground max-w-full truncate text-[11px] font-medium"
            title={column.label}
          >
            {column.label}
          </span>
        ) : (
          <>
            <span
              className="text-muted-foreground max-w-full truncate text-[10px] font-semibold tracking-wide uppercase"
              title={column.label}
            >
              {column.code ?? column.label}
            </span>
            {column.code ? (
              <span className="sr-only">{column.label}</span>
            ) : null}
          </>
        )}
      </span>
      {scoreMark}
    </span>
  );
  const className = cn(
    // 44px floor so the header meets the touch-target floor even with a short label.
    "flex h-full min-h-11 w-full min-w-0 items-end justify-center pb-1 transition-opacity",
    dimmed && "opacity-40",
  );

  const hoverHandlers = {
    onPointerEnter: (event: ReactPointerEvent<HTMLElement>) => {
      // A tap can't hover, and its click already goes somewhere — leave panels to the mouse.
      if (event.pointerType !== "mouse") return;
      hover.onOpen(event.currentTarget, false);
    },
    onPointerLeave: hover.onClose,
  };

  const trigger = onSelect ? (
    <button
      type="button"
      onClick={() => onSelect(column.id)}
      aria-label={ariaLabel}
      aria-haspopup="dialog"
      aria-expanded={hover.isOpen}
      aria-controls={hover.isOpen ? hover.panelId : undefined}
      {...hoverHandlers}
      onFocus={(event) => hover.onOpen(event.currentTarget, true)}
      onBlur={hover.onClose}
      className={cn(
        className,
        "hover:text-foreground focus-visible:ring-ring/40 cursor-pointer rounded-sm outline-none focus-visible:ring-[3px]",
      )}
    >
      {content}
    </button>
  ) : (
    <div className={className} {...hoverHandlers}>
      {content}
    </div>
  );

  return trigger;
}

/** Height of the preview strip when a creative has no thumbnail to show. */
const FALLBACK_PREVIEW_HEIGHT = "h-14";

/** Click-through promise per axis — a creative opens, a market scopes, a report navigates. */
const COLUMN_HINT_KEYS: Record<MatrixColumnKind, string> = {
  CREATIVE: "preflight.rail.hoverOpenHint",
  MARKET: "preflight.heatmap.hoverOpenMarket",
  REPORT: "preflight.heatmap.hoverOpenReport",
};

/**
 * What sits inside a column header's hover panel: the creative (or market / report) the
 * column stands for, with its score in context and how its criteria actually landed.
 */
function ColumnHeaderPanel({
  column,
  columnKind,
  tally,
  isSelectable,
}: {
  column: MatrixColumn;
  columnKind: MatrixColumnKind;
  tally: ColumnTally;
  isSelectable: boolean;
}) {
  const { t } = useTranslation();
  const isCreative = columnKind === "CREATIVE";
  const score = isCreative ? column.score : column.avgScore;
  const band = column.band ?? (score != null ? scoreToBand(score) : null);
  const tone = scoreTone(score, column.band);
  const iconKind =
    columnKind === "MARKET" ? marketColumnIconKind(column) : null;

  return (
    <div className="flex flex-col">
      {isCreative ? (
        <AssetThumbnail
          thumbnailUrl={column.thumbnailUrl}
          assetUrl={column.assetUrl}
          isVideo={column.isVideo}
          autoPlay
          iconSize="md"
          className="w-full"
          imageClassName="aspect-video"
          // A bare fallback glyph doesn't need a full 16:9 frame of empty grey.
          fallbackClassName={FALLBACK_PREVIEW_HEIGHT}
        />
      ) : null}

      <div className="flex flex-col gap-1 px-3 pt-2.5 pb-2">
        <div className="flex items-start gap-2">
          {iconKind && column.code ? (
            <MarketIcon
              kind={iconKind}
              code={column.code}
              name={column.label}
              className="mt-0.5"
            />
          ) : null}
          <AssetNameLabel
            name={column.label}
            className="text-foreground min-w-0 flex-1 text-xs font-medium"
          />
          {band ? <BandPill band={band} /> : null}
        </div>
        <span
          className={cn(
            "text-[11px] tabular-nums motion-safe:animate-in motion-safe:fade-in motion-safe:duration-150 motion-safe:ease-out",
            TONE_TEXT[tone],
          )}
        >
          {score != null
            ? t("preflight.summary.scoreOutOf", { score: Math.round(score) })
            : DASH}
        </span>
        {column.reportCount != null || column.creativeCount != null ? (
          <span className="text-muted-foreground text-[11px] tabular-nums">
            {[
              columnKind === "MARKET" && column.reportCount != null
                ? t("preflight.markets.reportCount", {
                    count: column.reportCount,
                  })
                : null,
              // NOTE: shares the score rail's creative-count copy so both read identically.
              column.creativeCount != null
                ? t("preflight.rail.zoneSegmentCount", {
                    count: column.creativeCount,
                  })
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
        ) : null}
      </div>

      <VerdictTally tally={tally} />

      {isSelectable ? (
        <p className="text-muted-foreground border-border/60 border-t px-3 py-1.5 text-[11px]">
          {t(COLUMN_HINT_KEYS[columnKind])}
        </p>
      ) : null}
    </div>
  );
}

const TALLY_ENTRIES = [
  { key: "passes", tone: "success", labelKey: "preflight.resultState.PASS" },
  {
    key: "partials",
    tone: "warning",
    labelKey: "preflight.resultState.PARTIAL",
  },
  { key: "failures", tone: "error", labelKey: "preflight.resultState.FAIL" },
] as const;

/** How the column's criteria landed: a proportional bar over the counts behind it. */
function VerdictTally({ tally }: { tally: ColumnTally }) {
  const { t } = useTranslation();
  const total = tally.passes + tally.partials + tally.failures;
  if (total === 0) return null;

  return (
    <div className="border-border/60 flex flex-col gap-1.5 border-t px-3 py-2">
      <span
        className="flex h-1 w-full overflow-hidden rounded-full"
        aria-hidden
      >
        {TALLY_ENTRIES.map((entry) => (
          <span
            key={entry.key}
            className={cn("block h-full", TONE_BG[entry.tone])}
            style={{ width: `${(tally[entry.key] / total) * 100}%` }}
          />
        ))}
      </span>
      <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] tabular-nums">
        {TALLY_ENTRIES.filter((entry) => tally[entry.key] > 0).map((entry) => (
          <span
            key={entry.key}
            className={cn(
              "inline-flex items-center gap-1",
              TONE_TEXT[entry.tone],
            )}
          >
            <span
              className={cn("size-1.5 rounded-full", TONE_BG[entry.tone])}
              aria-hidden
            />
            {tally[entry.key]} {t(entry.labelKey)}
          </span>
        ))}
      </span>
    </div>
  );
}

/** A creative carries its own band; an aggregate column derives one from its average. */
function scoreTone(
  score: number | null | undefined,
  band: PreflightScoreBand | null | undefined,
) {
  const resolved = band ?? (score != null ? scoreToBand(score) : null);
  return resolved ? bandTone(resolved) : "muted";
}

// Cap the diagonal reveal so wide/tall matrices still finish quickly; cells animate in
// along a top-left → bottom-right wave keyed off (row + column).
const WAVE_STEP_MS = 22;
const WAVE_MAX_STEPS = 22;

function RowCells({
  rowKey,
  rowIndex,
  name,
  definition,
  cells,
  columns,
  active,
  rowActive,
  cellPanelId,
  onActivate,
  onDeactivate,
  onSelect,
  t,
}: {
  rowKey: string;
  rowIndex: number;
  name: string;
  definition: string | null;
  cells: MatrixCell[];
  columns: MatrixColumn[];
  active: ActiveCell | null;
  rowActive: boolean;
  cellPanelId: string;
  onActivate: (
    cell: ActiveCell,
    trigger: HTMLElement,
    immediate: boolean,
  ) => void;
  onDeactivate: () => void;
  onSelect?: (columnId: string, criterionKey: string) => void;
  t: TFunction;
}) {
  return (
    <>
      <div
        className={cn(
          "flex min-w-0 items-center py-0.5 text-sm transition-colors",
          rowActive ? "text-foreground font-medium" : "text-muted-foreground",
        )}
        title={name}
      >
        <CriterionLabel
          name={name}
          definition={definition}
          className="min-w-0"
        />
      </div>
      {cells.map((cell, index) => {
        const column = columns[index];
        const tone = cellTone(cell);
        const isActive =
          active?.rowKey === rowKey && active.columnId === column.id;
        const dimmed = active != null && !isActive;
        const label = `${name}, ${column.label}: ${cellResultLabel(cell, t)}`;
        const waveDelay =
          Math.min(rowIndex + index, WAVE_MAX_STEPS) * WAVE_STEP_MS;
        return (
          <div key={column.id} className="h-7 min-h-7 overflow-clip">
            <button
              type="button"
              aria-label={label}
              aria-haspopup={onSelect ? "dialog" : undefined}
              aria-expanded={onSelect ? isActive : undefined}
              aria-controls={onSelect && isActive ? cellPanelId : undefined}
              onPointerEnter={(event) => {
                if (event.pointerType !== "mouse") return;
                onActivate(
                  { rowKey, columnId: column.id },
                  event.currentTarget,
                  false,
                );
              }}
              onPointerLeave={onDeactivate}
              onFocus={(event) =>
                onActivate(
                  { rowKey, columnId: column.id },
                  event.currentTarget,
                  true,
                )
              }
              onBlur={onDeactivate}
              onClick={onSelect ? () => onSelect(column.id, rowKey) : undefined}
              style={{
                animationDelay: `${waveDelay}ms`,
                animationDuration: "320ms",
              }}
              className={cn(
                "focus-visible:ring-ring/50 h-full w-full origin-center rounded-[3px] outline-none transition-[opacity,box-shadow,transform] duration-200 ease-out focus-visible:ring-2",
                "motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-75 motion-safe:fill-mode-both motion-reduce:transition-none motion-reduce:animate-none",
                "hover:scale-[1.18] hover:shadow-md",
                onSelect && "cursor-pointer",
                tone
                  ? TONE_BG[tone]
                  : "bg-muted/40 border-border/50 border border-dashed",
                tone && tone !== "muted" ? "opacity-90" : "",
                dimmed && "opacity-30",
                isActive && "ring-foreground/40 scale-[1.18] shadow-md ring-2",
              )}
            />
          </div>
        );
      })}
    </>
  );
}

/**
 * Detail card for one heatmap cell: criterion + column context, the verdict (or miss
 * tally), and a click hint when the cell navigates somewhere.
 */
function CellHoverPanel({
  criterion,
  definition,
  column,
  columnKind,
  cell,
  weight,
  isSelectable,
}: {
  criterion: string;
  definition: string | null;
  column: MatrixColumn;
  columnKind: MatrixColumnKind;
  cell: MatrixCell;
  weight: string;
  isSelectable: boolean;
}) {
  const { t } = useTranslation();
  const tone = cellTone(cell) ?? "muted";
  const verdicts = cellVerdicts(cell);
  const iconKind =
    columnKind === "MARKET" ? marketColumnIconKind(column) : null;
  const note = cell.feature ? featureNote(cell.feature, t) : null;
  const result = cell.feature
    ? featureResultLabel(cell.feature, t)
    : verdicts
      ? t("preflight.blockers.missedOf", {
          count: verdicts.missed,
          total: verdicts.total,
        })
      : t("preflight.heatmap.absent");

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-1 px-3 pt-2.5 pb-2">
        <span className="text-foreground text-xs font-medium">{criterion}</span>
        {definition ? (
          <p className="text-muted-foreground text-[11px] leading-snug">
            {definition}
          </p>
        ) : null}
        <div className="flex items-center gap-1.5">
          {iconKind && column.code ? (
            <MarketIcon
              kind={iconKind}
              code={column.code}
              name={column.label}
              className="h-3.5 w-[1.3rem] shrink-0 rounded-sm shadow-[0_0_0_1px_var(--border)]"
            />
          ) : null}
          <AssetNameLabel
            name={column.label}
            className="text-muted-foreground min-w-0 flex-1 text-[11px]"
          />
        </div>
        <span className="flex flex-wrap items-center gap-x-1.5 text-[11px] tabular-nums">
          <span className={TONE_TEXT[tone]}>{result}</span>
          <span className="text-muted-foreground">· {weight}</span>
        </span>
        {note ? (
          <p className="text-muted-foreground text-[11px] leading-snug">{note}</p>
        ) : null}
      </div>

      {verdicts && !cell.feature ? (
        <VerdictTally
          tally={{
            passes: cell.passes ?? 0,
            partials: cell.partials ?? 0,
            failures: cell.failures ?? 0,
          }}
        />
      ) : null}

      {isSelectable ? (
        <p className="text-muted-foreground border-border/60 border-t px-3 py-1.5 text-[11px]">
          {t("preflight.heatmap.hoverOpenCriterion")}
        </p>
      ) : null}
    </div>
  );
}

function CellReadout({
  criterion,
  columnLabel,
  tone,
  result,
  weight,
  note,
}: {
  criterion: string;
  columnLabel: string;
  tone: ResultTone;
  result: string;
  weight: string;
  /** Absent for aggregate cells, where there is no single verdict to explain. */
  note?: string;
}) {
  return (
    <span className="flex w-full min-w-0 items-center gap-1.5 whitespace-nowrap">
      <span
        className={cn("size-1.5 shrink-0 rounded-full", TONE_BG[tone])}
        aria-hidden
      />
      <span className="text-foreground shrink-0 font-medium">{criterion}</span>
      <span className="text-muted-foreground min-w-0 shrink truncate">
        · {columnLabel}
      </span>
      <span className={cn("shrink-0 tabular-nums", TONE_TEXT[tone])}>
        · {result}
      </span>
      <span className="text-muted-foreground shrink-0 tabular-nums">
        · {weight}
      </span>
      {note ? (
        <span className="text-muted-foreground min-w-0 truncate">· {note}</span>
      ) : null}
    </span>
  );
}

function Legend() {
  const { t } = useTranslation();
  const entries = [
    { tone: "success", labelKey: "preflight.resultState.PASS" },
    { tone: "warning", labelKey: "preflight.resultState.PARTIAL" },
    { tone: "error", labelKey: "preflight.resultState.FAIL" },
  ] as const;
  return (
    <div className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1">
      {entries.map((entry) => (
        <span
          key={entry.tone}
          className={cn(
            "inline-flex items-center gap-1.5",
            TONE_TEXT[entry.tone],
          )}
        >
          <span
            className={cn("size-2 rounded-[2px]", TONE_BG[entry.tone])}
            aria-hidden
          />
          {t(entry.labelKey)}
        </span>
      ))}
    </div>
  );
}
